import type { KeyboardEvent } from 'react';
import { useRef } from 'react';

import type { TabSize, TabVariant } from '@/components/Tab';
import { Tab } from '@/components/Tab';

import * as S from './TabList.styles';

export interface TabListItems {
  href?: string;
  id: string;
  label: string;
}

export interface TabListProps {
  activeItemId?: string;
  ariaLabel?: string;
  items: TabListItems[];
  onItemClick?: (id: string) => void;
  size?: TabSize;
  variant?: TabVariant;
}

export const getTabId = (id: string) => `tab-${id}`;
export const getTabPanelId = (id: string) => `tabpanel-${id}`;

// WAI-ARIA tabs pattern: Left/Right wrap around, Home/End jump to the ends
const getNextIndex = (key: string, currentIndex: number, lastIndex: number) => {
  switch (key) {
    case 'ArrowRight':
      return currentIndex >= lastIndex ? 0 : currentIndex + 1;
    case 'ArrowLeft':
      return currentIndex <= 0 ? lastIndex : currentIndex - 1;
    case 'Home':
      return 0;
    case 'End':
      return lastIndex;
    default:
      return null;
  }
};

export const TabList = ({
  activeItemId,
  ariaLabel,
  items,
  onItemClick,
  size,
  variant = 'underline',
}: TabListProps) => {
  const isSegmented = variant === 'segmented';
  const wrapperRef = useRef<HTMLElement>(null);

  // automatic activation: moving focus with the arrow keys also selects the tab
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const nextIndex = getNextIndex(
      event.key,
      items.findIndex(({ id }) => id === activeItemId),
      items.length - 1,
    );

    if (nextIndex === null) {
      return;
    }

    event.preventDefault();
    const nextId = items[nextIndex].id;
    onItemClick?.(nextId);
    wrapperRef.current
      ?.querySelector<HTMLElement>(`[id="${getTabId(nextId)}"]`)
      ?.focus();
  };

  return (
    <S.Wrapper
      ref={wrapperRef}
      aria-label={isSegmented ? ariaLabel : undefined}
      data-test-id='TabList'
      onKeyDown={isSegmented ? handleKeyDown : undefined}
      role={isSegmented ? 'tablist' : undefined}
      variant={variant}
    >
      {items.map(({ href, id, label, ...rest }, index) => {
        const isLink = !!href;
        const isActive = activeItemId === id;
        const props = isLink
          ? { href: href }
          : { onClick: () => onItemClick && onItemClick(id) };
        const ariaProps = isSegmented
          ? {
              'aria-controls': getTabPanelId(id),
              'aria-selected': isActive,
              id: getTabId(id),
              role: 'tab',
              // roving tabindex: only the active tab is in the tab sequence
              tabIndex: isActive ? 0 : -1,
            }
          : {};

        return (
          <Tab
            key={id}
            isActive={isActive}
            size={size}
            variant={variant}
            {...rest}
            {...props}
            {...ariaProps}
            data-test-id={`Tab-${index}`}
          >
            {label}
          </Tab>
        );
      })}
    </S.Wrapper>
  );
};

TabList.displayName = 'TabList';
